import { useEffect, useRef } from 'react'
import { checkAuthOnce } from './authCheck.js'

const STORAGE_KEY = 'conjugr8.progress.v1'

function countEvents(payload: Record<string, unknown>): number {
  let n = 0
  for (const v of Object.values((payload.byConjugation as Record<string, unknown[]>) ?? {})) n += v.length
  for (const v of Object.values((payload.vocabularyByTerm as Record<string, unknown[]>) ?? {})) n += v.length
  for (const v of Object.values((payload.bySkill as Record<string, unknown[]>) ?? {})) n += v.length
  return n
}

export function useProgressSync() {
  const syncing = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let mounted = true

    async function loadFromServer() {
      try {
        if (!(await checkAuthOnce())) return
        const res = await fetch('/api/data/progress')
        if (!res.ok) return
        const { payload } = await res.json()
        if (!payload) return

        const serverCount = countEvents(payload)
        if (serverCount === 0) return

        const localRaw = localStorage.getItem(STORAGE_KEY)
        const localCount = localRaw ? countEvents(JSON.parse(localRaw)) : 0

        if (serverCount > localCount && mounted) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
          window.dispatchEvent(new Event('c8:progress-loaded'))
        }
      } catch {}
    }

    async function syncToServer() {
      if (syncing.current) return
      syncing.current = true
      try {
        if (!(await checkAuthOnce())) return
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        await fetch('/api/data/progress', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: raw,
        })
      } catch {} finally {
        syncing.current = false
      }
    }

    function onProgressSaved() {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(syncToServer, 3000)
    }

    loadFromServer()
    window.addEventListener('c8:progress-saved', onProgressSaved)

    return () => {
      mounted = false
      window.removeEventListener('c8:progress-saved', onProgressSaved)
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])
}
